import React, { MouseEventHandler, Ref, useRef, useState, WheelEventHandler } from 'react'
import { isDesktop } from 'react-device-detect'
import { useTranslation } from 'react-i18next'
import { useStaticText, DescriptionItem } from '../../contexts/staticTextContext'
import { useContextUI } from '../../contexts/UIContext'
import { useEnv } from '../../utils/useEnv'
import { SafeHtml } from '../SafeHtml'
import { AboutProjectWrapper, ButtonWrapper, Container } from './common'
import CloseButton from '../CloseButton'
import { UIElement } from '../../types/UIElements'

const AboutProject = () => {
  const { t, i18n } = useTranslation()
  const { isKiosk } = useEnv()
  const { projectData } = useStaticText()
  const { filterOutUiElement } = useContextUI()
  const containerRef = useRef<HTMLDivElement>()
  const [wrapperClass, setWrapperClass] = useState<string>('fade-in')
  const [fadeOut, setFadeOut] = useState<boolean>(false)

  const lang = i18n.language

  const handleHorizontalWheelScroll: WheelEventHandler<HTMLDivElement> = event => {
    if (!containerRef?.current) return
    event.stopPropagation()
    containerRef.current.scrollLeft += event.deltaY
  }

  const handleClickOutside: MouseEventHandler<HTMLDivElement> = e => {
    if (e.target !== containerRef.current) return
    handleClose()
  }

  const handleClose = () => {
    setWrapperClass('fade-out')
    setFadeOut(true)
    window.history.replaceState(null, '', '/')
    setTimeout(() => {
      filterOutUiElement(UIElement.ABOUT)
      setFadeOut(false)
    }, 500)
  }

  return (
    <>
      <AboutProjectWrapper
        ref={containerRef as Ref<HTMLDivElement>}
        onWheel={isDesktop ? handleHorizontalWheelScroll : undefined}
        onClick={handleClickOutside}
        className={wrapperClass}
      >
        <Container>
          <div className={`wrapper ${fadeOut ? 'fade-out' : 'fade-in'}`}>
            {projectData.map((project, projectIndex) => (
              <React.Fragment key={projectIndex}>
                {project.multimedia &&
                  project.multimedia.length > 0 &&
                  project.multimedia.map((ref, refIndex) => (
                    <div
                      key={refIndex}
                      className={`${
                        projectIndex === 4
                          ? 'image-container-spread image-container'
                          : 'image-container'
                      }`}
                    >
                      <img src={ref.multimediaUri} alt="" />
                    </div>
                  ))}
                {(project[`description_${lang}` as keyof typeof project] as DescriptionItem[])
                  ?.length > 0 && (
                  <div className="about-project">
                    <main>
                      {projectIndex === 0 && (
                        <>
                          <h1 className="title">{t('aboutProject.title')}</h1>
                          <h2 className="subtitle">
                            {project[`title_${lang}` as keyof typeof project] || '\u00A0'}
                          </h2>
                        </>
                      )}
                      {projectIndex !== 0 && (
                        <>
                          <h1 className="title">
                            {project[`title_${lang}` as keyof typeof project] || '\u00A0'}
                          </h1>

                          <h2 className="subtitle">
                            {(
                              project[
                                `description_${lang}` as keyof typeof project
                              ] as DescriptionItem[]
                            )?.[0]?.notes?.toLowerCase() === 'h'
                              ? (
                                  project[
                                    `description_${lang}` as keyof typeof project
                                  ] as DescriptionItem[]
                                )?.[0]?.text
                              : '\u00A0'}
                          </h2>
                        </>
                      )}
                      {(
                        project[`description_${lang}` as keyof typeof project] as DescriptionItem[]
                      )?.map((item, index) => {
                        if (index === 0 && item.notes?.toLowerCase() === 'h') return null
                        const textWithBreaks = item.text?.replace(/\n/g, '<br />')
                        return (
                          <SafeHtml
                            key={index}
                            html={textWithBreaks || ''}
                            allowedTags={['a', 'b', 'br']}
                            allowedAttrs={['href']}
                            disableLinks={isKiosk}
                            className={`description${
                              item.notes?.toLowerCase() === 'm' ? ' addMargin' : ''
                            }`}
                          />
                        )
                      })}
                    </main>
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>
        </Container>
      </AboutProjectWrapper>
      <ButtonWrapper>
        <CloseButton onClick={handleClose} className={wrapperClass} />
      </ButtonWrapper>
    </>
  )
}

export default AboutProject
